<template>
  <div class="dvt" :class="'dvt--' + mode">
    <div v-for="c in visibleCategories" :key="c.key" class="dvt__cat">
      <div class="dvt__row dvt__row--cat">
        <button type="button" class="dvt__caret" :class="{ open: isOpen('c' + c.key) }" @click="toggle('c' + c.key)"></button>
        <label class="dvt__check">
          <input type="checkbox" :checked="catState(c) === 'all'" :indeterminate.prop="catState(c) === 'partial'" @change="setCategory(c, $event.target.checked)" />
        </label>
        <span class="dvt__name dvt__name--cat" @click="toggle('c' + c.key)">{{ c.name }}</span>
        <small class="dvt__meta">{{ c.visibleNodes.length }} 노드 · {{ catCount(c) }}</small>
      </div>

      <div v-show="isOpen('c' + c.key)" class="dvt__nodes">
        <div v-for="n in c.visibleNodes" :key="n.id" class="dvt__node">
          <div class="dvt__row dvt__row--node" :class="{ 'is-hl': nodeHighlighted(n) }">
            <button type="button" class="dvt__caret" :class="{ open: isOpen('n' + n.id) }" @click="toggle('n' + n.id)"></button>
            <label class="dvt__check">
              <input type="checkbox" :checked="nodeState(n) === 'all'" :indeterminate.prop="nodeState(n) === 'partial'" @change="setNode(n, $event.target.checked)" />
            </label>
            <span class="dvt__name" @click="toggle('n' + n.id)"><i v-if="n.order != null" class="dvt__order">{{ n.order }}</i>{{ n.name }}<small v-if="n.tail" class="dvt__tail"> {{ n.tail }}</small></span>
            <small class="dvt__meta">{{ nodeCount(n) }}</small>
          </div>

          <div v-show="isOpen('n' + n.id)" class="dvt__props">
            <div v-for="p in visibleProps(n)" :key="p.id" class="dvt__prop">
              <div class="dvt__row dvt__row--prop" :class="{ 'is-hl': hlSet.has(p.id) }" :data-pid="p.id">
                <button v-if="mode === 'filter' && p.isFacet" type="button" class="dvt__caret" :class="{ open: isOpen('p' + p.id) }" @click="toggle('p' + p.id)"></button>
                <span v-else class="dvt__caret dvt__caret--none"></span>
                <label class="dvt__check">
                  <input
                    type="checkbox"
                    :checked="propState(p) === 'all'"
                    :indeterminate.prop="propState(p) === 'partial'"
                    :disabled="mode === 'pick' && !isPicked(p) && pickFull"
                    @change="setProp(p, $event.target.checked)"
                  />
                </label>
                <span class="dvt__name dvt__name--prop" :title="propTitle(p)" @click="mode === 'filter' && p.isFacet ? toggle('p' + p.id) : setProp(p, propState(p) !== 'all')">
                  {{ p.name }}<small v-if="p.unit"> ({{ p.unit }})</small>
                </span>
                <small class="dvt__meta">{{ p.isNumeric ? p.validCount : p.textCount }}</small>
                <!-- 숫자 범위(필터 모드) -->
                <template v-if="mode === 'filter' && p.isNumeric">
                  <button v-if="!ranges[p.id]" type="button" class="dvt__range-add" :title="`${fmt(p.min)} ~ ${fmt(p.max)}`" @click="$emit('add-range', p)">범위</button>
                </template>
              </div>
              <div v-if="mode === 'filter' && p.isNumeric && ranges[p.id]" class="dvt__range">
                <input type="number" step="any" v-model.number="ranges[p.id].min" @change="$emit('range-change')" />
                <span>~</span>
                <input type="number" step="any" v-model.number="ranges[p.id].max" @change="$emit('range-change')" />
                <button type="button" title="범위 해제" @click="$emit('remove-range', p.id)">×</button>
              </div>
              <!-- 값 목록(필터 모드) -->
              <div v-if="mode === 'filter' && p.isFacet" v-show="isOpen('p' + p.id)" class="dvt__values">
                <label v-for="o in p.options" :key="o.value" class="dvt__value" :class="{ 'is-on': isValueOn(p, o.value) }">
                  <input type="checkbox" :checked="isValueOn(p, o.value)" @change="$emit('toggle-value', p.id, o.value)" />
                  <span class="dvt__value-text" :title="o.value">{{ o.value }}</span>
                  <small>{{ o.count }}</small>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 재료·공정·성능·분석 → 공정 노드 → 속성 → 값 트리.
 * mode = "filter": 값 체크(문자열 조건) + 숫자 범위. 각 단계 체크박스는 하위 전체 선택/해제(부분이면 중간 상태).
 * mode = "pick"  : 속성 단위 선택(표시 열·출력 항목). 상위 체크박스로 하위 속성 전체 선택/해제.
 */
export default {
  name: "DvFilterTree",
  props: {
    categories: { type: Array, required: true }, // [{ key, name, nodes: [{ id, name, props: [prop] }] }]
    mode: { type: String, default: "filter" },
    selected: { type: Object, default: () => ({}) }, // filter: propId → { value: true }
    ranges: { type: Object, default: () => ({}) },   // filter: propId → { min, max }
    picked: { type: Array, default: () => [] },      // pick: propId[]
    pickMax: { type: Number, default: 0 },
    numericOnly: { type: Boolean, default: false },
    openAll: { type: Boolean, default: false },
    highlight: { type: Array, default: () => [] },   // 강조할 속성 id(소자 그림 호버)
  },
  data() {
    return { open: {} };
  },
  computed: {
    pickFull() { return this.pickMax > 0 && this.picked.length >= this.pickMax; },
    hlSet() { return new Set(this.highlight); },
    visibleCategories() {
      return this.categories
        .map((c) => ({ ...c, visibleNodes: c.nodes.filter((n) => this.visibleProps(n).length) }))
        .filter((c) => c.visibleNodes.length);
    },
  },
  methods: {
    fmt(v) {
      if (v === null || v === undefined || !isFinite(v)) return "–";
      const a = Math.abs(v);
      if (a !== 0 && (a >= 1e6 || a < 1e-3)) return Number(v).toExponential(2);
      if (a >= 100) return Math.round(v).toLocaleString();
      return Number(Number(v).toFixed(a >= 1 ? 2 : 4)).toString();
    },
    isOpen(k) { return this.open[k] !== undefined ? this.open[k] : (this.openAll || k.startsWith("c")); },
    toggle(k) { this.$set(this.open, k, !this.isOpen(k)); },
    nodeHighlighted(n) { return this.hlSet.size > 0 && n.props.some((p) => this.hlSet.has(p.id)); },
    /** 주어진 속성들이 보이도록 분류·노드·속성(값 목록)을 펼친다 */
    openProps(pids) {
      const want = new Set(pids);
      this.categories.forEach((c) => c.nodes.forEach((n) => n.props.forEach((p) => {
        if (!want.has(p.id)) return;
        this.$set(this.open, "c" + c.key, true);
        this.$set(this.open, "n" + n.id, true);
        if (this.mode === "filter" && p.isFacet) this.$set(this.open, "p" + p.id, true);
      })));
    },
    visibleProps(n) {
      return n.props.filter((p) => {
        if (this.mode === "pick") return this.numericOnly ? p.isNumeric : (p.textCount > 0);
        return p.isFacet || p.isNumeric;
      });
    },
    propTitle(p) {
      if (p.isNumeric) return `숫자 · 유효 ${p.validCount}건 · ${this.fmt(p.min)} ~ ${this.fmt(p.max)}`;
      return `값 종류 ${p.options ? p.options.length : 0}개 · ${p.textCount}건`;
    },
    // ─── 상태 ───
    isValueOn(p, v) { return !!(this.selected[p.id] && this.selected[p.id][v]); },
    isPicked(p) { return this.picked.includes(p.id); },
    propState(p) {
      if (this.mode === "pick") return this.isPicked(p) ? "all" : "none";
      if (!p.isFacet) return this.ranges[p.id] ? "all" : "none";
      const n = Object.keys(this.selected[p.id] || {}).length;
      if (!n) return "none";
      return n >= p.options.length ? "all" : "partial";
    },
    aggregate(states) {
      const s = states.filter(Boolean);
      if (!s.length) return "none";
      if (s.every((x) => x === "all")) return "all";
      if (s.some((x) => x !== "none")) return "partial";
      return "none";
    },
    nodeState(n) { return this.aggregate(this.visibleProps(n).map((p) => this.propState(p))); },
    catState(c) { return this.aggregate((c.visibleNodes || c.nodes).map((n) => this.nodeState(n))); },
    nodeCount(n) {
      const ps = this.visibleProps(n);
      if (this.mode === "pick") return `${ps.filter((p) => this.isPicked(p)).length}/${ps.length}`;
      const on = ps.filter((p) => this.propState(p) !== "none").length;
      return on ? `${on}/${ps.length} 조건` : `${ps.length} 속성`;
    },
    catCount(c) {
      const ps = (c.visibleNodes || c.nodes).flatMap((n) => this.visibleProps(n));
      const on = ps.filter((p) => this.propState(p) !== "none").length;
      return this.mode === "pick" ? `${on}/${ps.length}` : (on ? `${on} 조건 적용` : `${ps.length} 속성`);
    },
    // ─── 하위 전체 선택/해제 ───
    setProp(p, on) {
      if (this.mode === "pick") { this.$emit("set-picks", [p.id], on); return; }
      if (p.isFacet) this.$emit("set-values", p.id, on ? p.options.map((o) => o.value) : null);
      else if (p.isNumeric) { if (on) this.$emit("add-range", p); else this.$emit("remove-range", p.id); }
    },
    setNode(n, on) {
      const ps = this.visibleProps(n);
      if (this.mode === "pick") { this.$emit("set-picks", ps.map((p) => p.id), on); return; }
      // 필터 모드: 문자열 조건은 전체 값, 숫자 속성은 전체 범위(min~max)로 — "값이 있는 행"만 남기는 효과
      ps.filter((p) => p.isFacet).forEach((p) => this.$emit("set-values", p.id, on ? p.options.map((o) => o.value) : null));
      ps.filter((p) => p.isNumeric).forEach((p) => { if (on) this.$emit("add-range", p); else if (this.ranges[p.id]) this.$emit("remove-range", p.id); });
    },
    setCategory(c, on) { (c.visibleNodes || c.nodes).forEach((n) => this.setNode(n, on)); },
  },
};
</script>

<style lang="scss" scoped>
.dvt { font-size: 12.5px; color: #223; }
.dvt__cat { border: 1px solid #dde6ec; border-radius: 10px; background: #fff; margin-bottom: 8px; overflow: hidden; }
.dvt__row { display: flex; align-items: center; gap: 6px; padding: 5px 8px; min-height: 28px;
  &.is-hl { background: #e6f7fc; box-shadow: inset 3px 0 0 #0dbde9; }
  &--cat { background: #f3f6f9; }
  &--node { padding-left: 14px; border-top: 1px solid #eef2f5; }
  &--prop { padding-left: 30px; }
}
.dvt__caret { width: 14px; height: 14px; flex: 0 0 14px; border: 0; background: none; cursor: pointer; position: relative; padding: 0;
  &::after { content: ""; position: absolute; left: 4px; top: 3px; border-left: 5px solid #789; border-top: 4px solid transparent; border-bottom: 4px solid transparent; transition: transform 0.12s; }
  &.open::after { transform: rotate(90deg); }
  &--none::after { display: none; }
}
.dvt__check { display: inline-flex; input { margin: 0; accent-color: #003f67; cursor: pointer; } }
.dvt__name { flex: 1 1 auto; cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  &--cat { font-weight: 700; color: #003f67; font-size: 13.5px; }
  &--prop { color: #334; small { color: #789; } }
}
.dvt__meta { color: #789; font-size: 11px; white-space: nowrap; }
.dvt__order { display: inline-block; min-width: 18px; margin-right: 6px; padding: 0 4px; border-radius: 4px; background: #e8f1fb; color: #1f4f8a; font-size: 10.5px; font-style: normal; text-align: center; line-height: 16px; }
.dvt__tail { color: #789; font-size: 11px; }
.dvt__range-add { padding: 1px 7px; border: 1px dashed #b8c7d1; border-radius: 999px; background: #fff; color: #556; font-size: 11px; cursor: pointer; &:hover { border-color: #0dbde9; color: #003f67; } }
.dvt__range { display: flex; align-items: center; gap: 4px; padding: 2px 8px 6px 52px; font-size: 12px;
  input { width: 78px; padding: 2px 6px; border: 1px solid #0dbde9; border-radius: 4px; font-size: 12px; }
  button { border: 0; background: none; color: #789; cursor: pointer; font-size: 14px; }
}
.dvt__values { padding: 0 8px 6px 52px; display: flex; flex-direction: column; gap: 1px; }
.dvt__value { display: flex; align-items: center; gap: 6px; padding: 2px 6px; border-radius: 6px; cursor: pointer; font-size: 12px; color: #334;
  input { margin: 0; accent-color: #003f67; }
  &-text { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  small { color: #789; }
  &.is-on { background: #e8f6fb; color: #003f67; }
  &:hover { background: #f6fbfe; }
}
.dvt--pick .dvt__row--prop { padding-left: 30px; }
</style>
